/**
 * G 夥伴管理（admin.partner 權限；PRD §G / AC-G-1 / AC-G-6）。
 *
 * 本模組有兩處「機密只出現一次」的回應（建立 / 重產金鑰的 `api_key`、
 * 設定 / 輪替 webhook 的 secret）。呼叫端必須當場交付給使用者，不可只寫進
 * log 或預期之後能再查——後端只保存雜湊。
 */

import { apiFetch, type Schemas } from './core';

export type PartnerTier = Schemas['PartnerResponseDto']['tier'];
export type Partner = Schemas['PartnerResponseDto'];

/**
 * 建立 / 重產金鑰的回應。
 *
 * `api_key` 是**唯一一次**能拿到明碼的機會 —— 後端只存 sha256，離開這個回應
 * 就再也取不回。呼叫端必須當場交付給使用者，不可只寫進 log 或預期之後能再查。
 */
export type PartnerWithSecret = Schemas['PartnerWithSecretResponseDto'];

export interface CreatePartnerDto {
  partner_code: string;
  display_name: string;
  tier?: PartnerTier;
}

/** AC-G-6：各級別的預設配額，建立前先讓使用者知道選了會拿到什麼。 */
export const PARTNER_TIER_DEFAULTS: Record<
  PartnerTier,
  { quotaPerMinute: number; quotaPerMonth: number; maxActiveHolds: number; holdTtlSeconds: number }
> = {
  T1: { quotaPerMinute: 60, quotaPerMonth: 100_000, maxActiveHolds: 5, holdTtlSeconds: 300 },
  T2: { quotaPerMinute: 120, quotaPerMonth: 500_000, maxActiveHolds: 20, holdTtlSeconds: 600 },
  T3: { quotaPerMinute: 300, quotaPerMonth: 2_000_000, maxActiveHolds: 100, holdTtlSeconds: 900 },
};

export function listPartners(): Promise<{ items: Partner[] }> {
  return apiFetch<{ items: Partner[] }>('/admin/partners', { method: 'GET' });
}

export function createPartner(dto: CreatePartnerDto): Promise<PartnerWithSecret> {
  return apiFetch<PartnerWithSecret>('/admin/partners', { method: 'POST', body: dto });
}

const partnerPath = (id: string, suffix = ''): string =>
  `/admin/partners/${encodeURIComponent(id)}${suffix}`;

export function getPartner(id: string): Promise<Partner> {
  return apiFetch<Partner>(partnerPath(id), { method: 'GET' });
}

/** 改級別會**連動重設**配額為該級別預設，不是只換標籤。 */
export function setPartnerTier(id: string, tier: PartnerTier): Promise<Partner> {
  return apiFetch<Partner>(partnerPath(id, '/tier'), { method: 'POST', body: { tier } });
}

/** 停權 / 復權；後端一律寫入人工核准 Log（AC-G-10）。 */
export function setPartnerStatus(
  id: string,
  status: 'active' | 'suspended',
  reason?: string,
): Promise<Partner> {
  return apiFetch<Partner>(partnerPath(id, '/status'), {
    method: 'POST',
    body: reason ? { status, reason } : { status },
  });
}

export type SuspensionApproval = Schemas['SuspensionApprovalResponseDto'];

export function listPartnerSuspensionLog(id: string): Promise<{ items: SuspensionApproval[] }> {
  return apiFetch<{ items: SuspensionApproval[] }>(partnerPath(id, '/suspension-log'), {
    method: 'GET',
  });
}

/** 重產 API Key：**舊 Key 立即失效**，夥伴已上線的串接會中斷。 */
export function regeneratePartnerKey(id: string): Promise<PartnerWithSecret> {
  return apiFetch<PartnerWithSecret>(partnerPath(id, '/regenerate-key'), { method: 'POST' });
}

export type WebhookSecretResult = Schemas['WebhookSecretResponseDto'];

/** 設定 Webhook URL；已有 secret 時後端視同輪替，一併回新 secret。 */
export function setPartnerWebhook(id: string, webhookUrl: string): Promise<WebhookSecretResult> {
  return apiFetch<WebhookSecretResult>(partnerPath(id, '/webhook'), {
    method: 'POST',
    body: { webhook_url: webhookUrl },
  });
}

/** 只換 secret 不動 URL；舊 secret 於重疊期內仍有效。 */
export function rotatePartnerWebhookSecret(id: string): Promise<WebhookSecretResult> {
  return apiFetch<WebhookSecretResult>(partnerPath(id, '/webhook/rotate-secret'), {
    method: 'POST',
  });
}

export type WebhookDispatchResult = Schemas['WebhookDispatchResponseDto'];

export function testPartnerWebhook(id: string): Promise<WebhookDispatchResult> {
  return apiFetch<WebhookDispatchResult>(partnerPath(id, '/webhook/test'), { method: 'POST' });
}

/** 解除連續失敗導致的自動暫停。 */
export function resumePartnerWebhook(id: string): Promise<Partner> {
  return apiFetch<Partner>(partnerPath(id, '/webhook/resume'), { method: 'POST' });
}

export type PartnerAllocation = Schemas['PartnerAllocationResponseDto'];

export function listPartnerAllocations(id: string): Promise<{ items: PartnerAllocation[] }> {
  return apiFetch<{ items: PartnerAllocation[] }>(partnerPath(id, '/allocations'), {
    method: 'GET',
  });
}

export function setPartnerAllocation(
  id: string,
  scheduleId: string,
  allocatedQty: number,
): Promise<PartnerAllocation> {
  return apiFetch<PartnerAllocation>(partnerPath(id, '/allocations'), {
    method: 'POST',
    body: { schedule_id: scheduleId, allocated_qty: allocatedQty },
  });
}
